<template>
  <div>
    <b-modal
      v-model="showModal"
      id="modal-form"
      class="mc-modal alert-config-modal"
      size="lg"
      centered
      :hide-header-close="true"
      no-close-on-backdrop
      body-class="p-0"
      header-class="p-0 border-0"
    >
      <template #modal-header="{ close }">
        <div class="dic-header w-100 d-flex justify-content-between align-items-center px-4 py-3">
          <div class="dic-header-text">
            <h5 class="modal-title text-white fw-bold mb-0">Konfigurasi Media Evidence</h5>
          </div>
          <button
            type="button"
            class="btn-close btn-close-white shadow-none"
            aria-label="Close"
            @click="close()"
          ></button>
        </div>
      </template>

      <div v-if="photoSensorError" class="dic-error">
        <i class="fas fa-exclamation-triangle fa-3x mb-3 text-warning" />
        <p class="fw-600 fs-5 mb-2">{{ photoSensorErrorTitle }}</p>
        <p class="text-muted small mb-3">{{ photoSensorErrorMessage }}</p>
        <b-button variant="outline-primary" size="sm" @click="refreshAlertCodes">
          <i class="fas fa-sync me-1" /> Coba Refresh
        </b-button>
      </div>

      <div v-else-if="!isDataReady" class="dic-loading">
        <b-spinner variant="primary" class="mb-3" />
        <p class="mb-1 fw-500">Memuat data konfigurasi…</p>
        <small class="text-muted">
          Alert Codes {{ isLoadingAlertCodes ? '⏳' : '✅' }} &nbsp;·&nbsp; Sensors
          {{ isLoadingSensors ? '⏳' : '✅' }} &nbsp;·&nbsp; Device
          {{ isLoadingDeviceCamera ? '⏳' : '✅' }}
        </small>
      </div>

      <div v-else class="dic-body">
        <div class="dic-info">
          <div class="dic-info__icon">
            <i class="fas fa-info-circle" />
          </div>
          <div class="dic-info__text">
            Pilih <strong>alert</strong> yang memicu pengambilan media, lalu tentukan
            <strong>kamera</strong> untuk setiap alert. Jenis media mengikuti
            <strong>sensor code</strong> yang aktif pada device. Kamera wajib pada alert ADAS dan
            DMS tidak dapat dinonaktifkan.
          </div>
        </div>

        <div class="dic-toolbar">
          <div class="dic-toolbar__row">
            <div class="dic-search">
              <i class="fas fa-search dic-search__icon" />
              <input
                v-model="searchQuery"
                type="text"
                class="form-control dic-search__input"
                placeholder="Cari alert berdasarkan nama atau code…"
                :disabled="loading"
              />
              <button v-if="searchQuery" class="dic-search__clear" @click="searchQuery = ''">
                <i class="fas fa-times" />
              </button>
            </div>

            <div class="dic-filter-wrapper">
              <input
                type="checkbox"
                v-model="showOnlySelected"
                id="filter-selected"
                class="dic-filter-input"
              />
              <label for="filter-selected" class="dic-filter-label">
                <span class="dic-filter-checkmark"></span>
                <span class="dic-filter-text">Hanya yang dipilih</span>
              </label>
            </div>
          </div>

          <div class="dic-toolbar__row dic-toolbar__row--between">
            <div class="dic-summary">
              <span class="dic-badge"> {{ selectedCount }} / {{ totalCount }} </span>
              <span class="text-muted small">alert dipilih</span>
              <span v-if="invalidAlerts.length" class="ms-3 small text-danger">
                <i class="fas fa-exclamation-triangle me-1" />
                {{ invalidAlerts.length }} alert belum punya kamera
              </span>
              <b-button
                v-if="applicableCategories && applicableCategories.length === 0"
                size="sm"
                variant="outline-secondary"
                class="ms-3"
                @click="refreshAlertCodes"
              >
                <i class="fas fa-sync me-1" /> Refresh
              </b-button>
            </div>

            <div class="dic-toolbar__actions">
              <b-button
                size="sm"
                variant="outline-secondary"
                @click="expandAllCategories"
                :disabled="loading || applicableCategories.length === 0"
                class="me-1"
                title="Buka semua kategori"
              >
                <i class="fas fa-expand-alt" />
              </b-button>
              <b-button
                size="sm"
                variant="outline-secondary"
                @click="collapseAllCategories"
                :disabled="loading"
                class="me-2"
                title="Tutup semua kategori"
              >
                <i class="fas fa-compress-alt" />
              </b-button>
              <b-button
                size="sm"
                variant="outline-primary"
                @click="selectAll"
                :disabled="loading || alertCodes.length === 0"
                class="me-1"
              >
                <i class="fas fa-check-double me-1" /> Pilih Semua
              </b-button>
              <b-button
                size="sm"
                variant="outline-danger"
                @click="deselectAll"
                :disabled="loading || selectedCount === 0"
              >
                <i class="fas fa-times me-1" /> Hapus Semua
              </b-button>
            </div>
          </div>
        </div>

        <div class="dic-list">
          <div v-if="applicableCategories.length === 0" class="dic-empty dic-empty--full">
            <i class="fas fa-database fa-3x mb-3 text-muted" />
            <p class="fw-600 fs-5">Belum ada alert yang tersedia</p>
            <p class="text-muted small">
              Tidak ada konfigurasi pengambilan gambar pada perangkat ini. Pastikan perangkat
              mendukung fitur Image Capture (sensor kode {{ picturesSensorCode }},
              {{ oneSecondVideoSensorCode }}, atau {{ historicalVideoSensorCode }}).
            </p>
            <b-button variant="outline-primary" size="sm" @click="refreshAlertCodes">
              <i class="fas fa-sync me-1" /> Coba Refresh
            </b-button>
          </div>

          <div v-else-if="filteredCategories.length === 0" class="dic-empty">
            <i class="fas fa-inbox fa-2x mb-3 text-muted" />
            <p class="mb-1 fw-500">Tidak ada alert yang cocok</p>
            <small class="text-muted">Coba ubah kata kunci atau nonaktifkan filter.</small>
          </div>

          <div
            v-for="cat in filteredCategories"
            :key="`cat-${cat.alert_category_id}`"
            class="dic-category"
          >
            <div
              class="dic-category__header"
              @click="toggleCategoryExpanded(cat.alert_category_id)"
            >
              <div class="dic-category__check" @click.stop>
                <input
                  type="checkbox"
                  :checked="isCategoryFullySelected(cat)"
                  :indeterminate.prop="isCategoryPartiallySelected(cat)"
                  @change="toggleCategorySelection(cat)"
                  :id="`cat-check-${cat.alert_category_id}`"
                  class="dic-card__checkbox"
                />
              </div>

              <div class="dic-category__icon">
                <i class="fas" :class="getCategoryIcon(cat)" />
              </div>

              <div class="dic-category__title">
                <div class="dic-category__name-row">
                  <span class="dic-category__name">{{ cat.category_eng }}</span>
                  <span class="dic-category__badge">
                    {{ getCategorySelectedCount(cat) }}/{{ cat.alert_codes.length }}
                  </span>
                </div>
                <div class="dic-category__subtitle">
                  {{ cat.description_eng || '—' }}
                </div>
              </div>

              <button
                type="button"
                class="dic-category__toggle"
                :aria-expanded="isCategoryExpanded(cat.alert_category_id) ? 'true' : 'false'"
                @click.stop="toggleCategoryExpanded(cat.alert_category_id)"
                :title="
                  isCategoryExpanded(cat.alert_category_id) ? 'Tutup kategori' : 'Buka kategori'
                "
              >
                <i
                  class="fas"
                  :class="
                    isCategoryExpanded(cat.alert_category_id) ? 'fa-chevron-up' : 'fa-chevron-down'
                  "
                />
              </button>
            </div>

            <b-collapse :visible="isCategoryExpanded(cat.alert_category_id)">
              <div class="dic-category__body">
                <div
                  v-for="alertCode in cat.alert_codes"
                  :key="`alert-${alertCode.alert_code}`"
                  class="dic-card"
                  :class="{
                    'is-selected': isAlertSelected(alertCode.alert_code),
                    'is-invalid':
                      isAlertSelected(alertCode.alert_code) &&
                      isAlertConfigReady(alertCode.alert_code) &&
                      getSafeCameraConfig(alertCode.alert_code).cameras.length === 0,
                  }"
                >
                  <div class="dic-card__header" @click="toggleAlert(alertCode.alert_code)">
                    <div class="dic-card__check" @click.stop>
                      <input
                        type="checkbox"
                        :checked="isAlertSelected(alertCode.alert_code)"
                        @change="toggleAlert(alertCode.alert_code)"
                        :id="`check-${alertCode.alert_code}`"
                        class="dic-card__checkbox"
                      />
                    </div>

                    <div class="dic-card__title">
                      <div class="dic-card__name">{{ alertCode.description_eng }}</div>
                      <div class="dic-card__meta">
                        <span class="dic-chip">Code {{ alertCode.alert_code }}</span>
                        <span
                          v-if="
                            isAlertSelected(alertCode.alert_code) &&
                            getCameraLabel(alertCode.alert_code)
                          "
                          class="dic-chip dic-chip--info"
                        >
                          <i class="fas fa-video me-1" />
                          Kamera: {{ getCameraLabel(alertCode.alert_code) }}
                        </span>
                        <span
                          v-else-if="isAlertSelected(alertCode.alert_code)"
                          class="dic-chip dic-chip--warning"
                        >
                          <i class="fas fa-exclamation-circle me-1" />
                          Belum ada kamera
                        </span>
                        <span
                          v-if="isAlertSelected(alertCode.alert_code)"
                          class="dic-chip dic-chip--media"
                          :class="{
                            'dic-chip--media-video': isMediaActive(alertCode.alert_code, 'video'),
                          }"
                        >
                          <i
                            class="fas"
                            :class="
                              isMediaActive(alertCode.alert_code, 'video') ? 'fa-film' : 'fa-camera'
                            "
                          />
                          {{ getMediaLabel(alertCode.alert_code) }}
                          <span class="dic-chip__sub">
                            · sensor {{ getSensorCodeFor(alertCode.alert_code) }}
                          </span>
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      class="dic-card__toggle"
                      :aria-expanded="isExpanded(alertCode.alert_code) ? 'true' : 'false'"
                      @click.stop="toggleExpanded(alertCode.alert_code)"
                      :disabled="!isAlertSelected(alertCode.alert_code)"
                      :title="
                        isAlertSelected(alertCode.alert_code)
                          ? 'Buka / tutup pengaturan kamera'
                          : 'Pilih alert terlebih dahulu'
                      "
                    >
                      <i
                        class="fas"
                        :class="
                          isExpanded(alertCode.alert_code) ? 'fa-chevron-up' : 'fa-chevron-down'
                        "
                      />
                    </button>
                  </div>

                  <b-collapse
                    :visible="
                      isAlertSelected(alertCode.alert_code) &&
                      isExpanded(alertCode.alert_code) &&
                      isAlertConfigReady(alertCode.alert_code)
                    "
                  >
                    <div class="dic-card__body">
                      <!-- Media type selector — hidden for profile (ADAS/DMS) alerts -->
                      <div v-if="!isProfileAlert(alertCode.alert_code)" class="dic-media">
                        <div class="dic-section-title mb-2">
                          <i class="fas fa-photo-video me-2" /> Jenis Media
                        </div>
                        <div class="dic-media-group" role="group" aria-label="Jenis media">
                          <button
                            type="button"
                            class="dic-media-btn"
                            :class="{ 'is-active': isMediaActive(alertCode.alert_code, 'photo') }"
                            @click="setMedia(alertCode.alert_code, 'photo')"
                            :title="`Ambil foto saja (sensor code ${getDefaultPhotoSensorCode()})`"
                          >
                            <i class="fas fa-camera" />
                            <span>Foto</span>
                          </button>
                          <button
                            type="button"
                            class="dic-media-btn"
                            :class="{ 'is-active': isMediaActive(alertCode.alert_code, 'video') }"
                            @click="setMedia(alertCode.alert_code, 'video')"
                            :title="`Foto + Video (sensor code ${historicalVideoSensorCode}). Foto otomatis aktif.`"
                            v-if="hasHistoricalVideoSensor"
                          >
                            <i class="fas fa-film" />
                            <span>Video</span>
                          </button>
                          <div v-if="!hasHistoricalVideoSensor" class="dic-media-notice">
                            <i class="fas fa-info-circle me-1" />
                            <small>
                              Perangkat ini tidak mendukung pengambilan video (sensor 40 tidak
                              tersedia).
                            </small>
                          </div>
                          <div class="dic-media-info">
                            <i class="fas fa-info-circle me-1" />
                            <span>
                              Sensor code aktif:
                              <strong>{{ getSensorCodeFor(alertCode.alert_code) }}</strong>
                              <span class="text-muted">
                                ({{ getMediaLabel(alertCode.alert_code) }})
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>

                      <!-- Profile alert info -->
                      <div v-else class="dic-media dic-media--profile">
                        <div class="dic-section-title mb-2">
                          <i class="fas fa-shield-alt me-2" /> Profil ADAS / DMS
                        </div>
                        <div class="dic-media-info">
                          <span>
                            Alert ini terikat ke
                            <strong>sensor {{ getSensorCodeFor(alertCode.alert_code) }}</strong>
                            (profil ADAS/DMS). Media: <strong>Foto + Video</strong>. Kamera wajib
                            aktif dan tidak dapat dinonaktifkan.
                          </span>
                        </div>
                      </div>

                      <div class="dic-section-head">
                        <div class="dic-section-title">
                          <i class="fas fa-video me-2" /> Pilih Kamera
                        </div>
                        <div v-if="isCameraSectionReady" class="dic-section-actions">
                          <b-button
                            size="sm"
                            variant="link"
                            class="p-0 me-3"
                            @click="selectAllCamerasFor(alertCode.alert_code)"
                          >
                            Semua
                          </b-button>
                          <b-button
                            size="sm"
                            variant="link"
                            class="p-0 text-danger"
                            @click="clearCamerasFor(alertCode.alert_code)"
                          >
                            Kosongkan
                          </b-button>
                        </div>
                      </div>

                      <div
                        v-if="getChannelsForAlert(alertCode.alert_code).length === 0"
                        class="dic-no-cam"
                      >
                        <i class="fas fa-exclamation-circle me-2" />
                        Tidak ada kamera yang tersedia untuk kendaraan ini
                      </div>

                      <div v-else class="dic-camera-grid">
                        <label
                          v-for="cam in getChannelsForAlert(alertCode.alert_code)"
                          :key="`cam-${alertCode.alert_code}-${cam.value}`"
                          class="dic-camera"
                          :class="{
                            'is-active': isCameraChecked(alertCode.alert_code, cam.value),
                          }"
                        >
                          <input
                            type="checkbox"
                            :checked="isCameraChecked(alertCode.alert_code, cam.value)"
                            :disabled="cam.isDefault"
                            @change="toggleCamera(alertCode.alert_code, cam.value)"
                          />
                          <i class="fas fa-video" />
                          <span>{{ cam.text }}</span>
                        </label>
                      </div>

                      <div
                        v-if="
                          isCameraSectionReady &&
                          getSafeCameraConfig(alertCode.alert_code).cameras.length === 0
                        "
                        class="dic-inline-error"
                      >
                        <i class="fas fa-exclamation-triangle me-1" />
                        Pilih minimal 1 kamera untuk alert ini.
                      </div>
                    </div>
                  </b-collapse>
                </div>
              </div>
            </b-collapse>
          </div>
        </div>
      </div>

      <template #modal-footer="{ cancel }">
        <div class="dic-footer">
          <div class="dic-footer__status">
            <span v-if="photoSensorError" class="text-danger">
              <i class="fas fa-exclamation-triangle me-2" />
              Perbaiki konfigurasi sensor terlebih dahulu
            </span>
            <span v-else-if="hasChanges" class="text-warning">
              <i class="fas fa-circle me-2 pulse-dot" />
              Ada perubahan yang belum disimpan
            </span>
            <span v-else class="text-muted">
              <i class="fas fa-check-circle me-2" />
              Tidak ada perubahan
            </span>
          </div>
          <div class="dic-footer__actions">
            <b-button variant="outline-secondary" @click="cancel()" :disabled="loading">
              Batal
            </b-button>
            <b-button
              variant="primary"
              @click="handleSubmit"
              :disabled="
                loading ||
                isDisabledSaveButton ||
                !isCameraValid() ||
                !isDataReady ||
                photoSensorError ||
                alertCodes.length === 0
              "
            >
              <b-spinner v-if="loading" small class="me-2" />
              <i v-else class="fas fa-save me-2" />
              {{ loading ? 'Menyimpan…' : 'Simpan Perubahan' }}
            </b-button>
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { validationMixin } from 'vuelidate'
import * as sensorApi from '@/services/rest-api-sms/lib/sensor/sensor'
import * as masterSensorApi from '@/services/rest-api-tools/lib/masterdata/sensor'
import * as sensorAlertApi from '@/services/rest-api-sms/lib/sensor-alert/sensor-alert'
import * as deviceCameraApi from '@/services/rest-api-sms/lib/device-camera/device-camera'

export default {
  mixins: [validationMixin],

  props: {
    title: { type: String, default: 'Konfigurasi Media Evidence' },
    modal: { type: Boolean, default: false },
    isLoading: { type: Boolean, default: false },
    vehicleId: { type: Number, default: null },
    deviceCode: { type: Number, default: null },
    childVehicleId: { type: Number, default: null },
    region: { type: String, default: 'SG' },
  },

  data() {
    return {
      pinCode: null,
      sensorCode: null,
      searchQuery: '',
      form: {
        status_text: null,
        alertCodeConfig: {},
      },

      alertCategories: [],
      expandedCategories: [],
      alertCodes: [],

      selectedAlertCodes: [],
      originalForm: { status_text: null, alertCodeConfig: {} },
      modifiedFields: {},
      isDisabled: true,
      isDisabledSaveButton: true,
      hasUserMadeChanges: false,
      modalConfirm: false,
      availableCameras: [],
      statusOptions: [
        { id: true, name: 'Ya' },
        { id: false, name: 'Tidak' },
      ],
      statusText: null,
      picturesSensorCode: 37,
      oneSecondVideoSensorCode: 75,
      historicalVideoSensorCode: 40,
      cameraListSensorCode: 35,
      isLoadingAlertCodes: false,
      isLoadingSensors: false,
      dataInitialized: false,
      rawAlertCodeConfig: null,
      rawStatusText: null,
      isConfigReady: false,
      childSensors: [],
      sensors: [],
      expandedAlerts: [],
      showOnlySelected: false,
      refreshAttempt: 0,

      deviceCamera: null,
      isLoadingDeviceCamera: false,

      activePhotoSensorCode: null,
      photoSensorError: null, // 'BOTH_ACTIVE' | 'NONE_ACTIVE' | null
    }
  },

  computed: {
    showModal: {
      get() {
        return this.modal
      },
      set(newVal) {
        this.$emit('update:modal', newVal)
      },
    },
    loading: {
      get() {
        return this.isLoading
      },
      set(newVal) {
        this.$emit('update:isLoading', newVal)
      },
    },

    isDataReady() {
      return (
        !this.photoSensorError &&
        this.alertCategories !== null &&
        this.sensors.length > 0 &&
        this.childSensors.length > 0 &&
        !this.isLoadingAlertCodes &&
        !this.isLoadingSensors &&
        !this.isLoadingDeviceCamera &&
        this.isConfigReady
      )
    },

    applicableCategories() {
      if (!Array.isArray(this.alertCategories)) return []
      return this.alertCategories.filter((cat) => this.isCategoryApplicable(cat))
    },

    filteredCategories() {
      if (!this.applicableCategories || this.applicableCategories.length === 0) return []
      const q = this.searchQuery ? this.searchQuery.toLowerCase() : ''
      const selected = new Set(this.selectedAlertCodes.map((c) => Number(c)))
      const result = []

      this.applicableCategories.forEach((cat) => {
        const alerts = (cat.alert_codes || []).filter((a) => {
          if (this.showOnlySelected && !selected.has(Number(a.alert_code))) return false
          if (q) {
            const nameMatch = (a.description_eng || '').toLowerCase().includes(q)
            const codeMatch = String(a.alert_code).includes(q)
            if (!nameMatch && !codeMatch) return false
          }
          return true
        })

        if (alerts.length > 0) {
          result.push({ ...cat, alert_codes: alerts })
        }
      })

      return result
    },

    hasChanges() {
      return this.hasUserMadeChanges
    },

    isCameraSectionReady() {
      return this.isDataReady && this.availableCameras.length > 0
    },

    selectedCount() {
      return this.selectedAlertCodes.length
    },

    totalCount() {
      return this.applicableCategories.reduce((sum, cat) => sum + (cat.alert_codes || []).length, 0)
    },

    invalidAlerts() {
      const applicableCodes = new Set(
        this.applicableCategories.flatMap((c) =>
          (c.alert_codes || []).map((a) => Number(a.alert_code))
        )
      )
      return this.selectedAlertCodes.filter((code) => {
        if (!applicableCodes.has(Number(code))) return false
        const cfg = this.form.alertCodeConfig[Number(code)]
        if (!cfg || !Array.isArray(cfg.cameras) || cfg.cameras.length === 0) return true
        const def = this.getDefaultChannelForAlert(code)
        if (def != null && cfg.cameras.map(String).includes(def)) return false
        return false
      })
    },

    isMultiImei() {
      return !!(this.childVehicleId && Number(this.vehicleId) !== Number(this.childVehicleId))
    },

    hasHistoricalVideoSensor() {
      const allSensors = [...this.sensors, ...this.childSensors]
      return allSensors.some((s) => Number(s.sensor_code) === this.historicalVideoSensorCode)
    },

    isDeviceCameraReady() {
      return !!this.deviceCamera
    },

    supportAdas() {
      return this.parseBoolean(this.deviceCamera?.is_support_adas)
    },

    supportDms() {
      return this.parseBoolean(this.deviceCamera?.is_support_dms)
    },

    supportDrivingBehaviour() {
      return true
    },

    adasDefaultChannel() {
      const v = this.deviceCamera?.default_channel_adas
      return v != null && v !== '' ? String(v) : null
    },

    dmsDefaultChannel() {
      const v = this.deviceCamera?.default_channel_dms
      return v != null && v !== '' ? String(v) : null
    },

    photoSensorErrorTitle() {
      if (this.photoSensorError === 'BOTH_ACTIVE') return 'Konfigurasi Sensor Tidak Valid'
      if (this.photoSensorError === 'NONE_ACTIVE') return 'Tidak Ada Sensor Gambar Aktif'
      return ''
    },

    photoSensorErrorMessage() {
      if (this.photoSensorError === 'BOTH_ACTIVE') {
        return `Kedua sensor gambar (Sensor ${this.picturesSensorCode} dan Sensor ${this.oneSecondVideoSensorCode}) sedang aktif. Hanya salah satu yang boleh aktif. Silakan nonaktifkan salah satu sensor terlebih dahulu, lalu klik Coba Refresh.`
      }
      if (this.photoSensorError === 'NONE_ACTIVE') {
        return `Tidak ada sensor gambar yang aktif. Salah satu dari Sensor ${this.picturesSensorCode} atau Sensor ${this.oneSecondVideoSensorCode} harus aktif untuk menggunakan fitur Image Capture.`
      }
      return ''
    },
  },

  watch: {
    showModal: {
      immediate: true,
      async handler(val) {
        if (!val) return
        this.dataInitialized = false
        await this.$nextTick()
        if (this.vehicleId) await this.getSensors()
        await this.fetchDeviceCamera()
        await this.getAlertCodes()
        this.initModalState()
      },
    },
    vehicleId: {
      async handler(val, oldVal) {
        if (!val) return
        if (val === oldVal) return
        await this.getAlertCodes()
        await this.getSensors()
      },
    },
    deviceCode: {
      async handler(val, oldVal) {
        if (val === oldVal) return
        await this.fetchDeviceCamera()
      },
    },

    deviceCamera() {
      this.$nextTick(() => this.applyDefaultChannelsToConfig())
    },
    alertCategories() {
      this.$nextTick(() => this.applyDefaultChannelsToConfig())
    },
  },

  async mounted() {
    if (this.showModal && !this.dataInitialized) {
      this.dataInitialized = true
    } else {
      await this.loadInitialData()
      if (this.vehicleId) await this.getSensors()
    }
  },

  methods: {
    parseBoolean(val) {
      if (val === true) return true
      if (val === false) return false
      if (typeof val === 'string') {
        const v = val.trim().toLowerCase()
        return v === 'true' || v === '1' || v === 'yes'
      }
      if (typeof val === 'number') return val === 1
      return false
    },

    getDefaultPhotoSensorCode() {
      return this.activePhotoSensorCode || this.oneSecondVideoSensorCode
    },

    // ✅ ADAS/DMS selalu sensor 40 (foto + video). Bukan 58/59.
    getNaturalSensorCodeFor(alertCode) {
      const num = Number(alertCode)

      // ── 1. PRIORITAS: ADAS/DMS → sensor 40 ──
      if (this.isProfileAlert(num)) {
        return this.historicalVideoSensorCode // = 40
      }

      // ── 2. API sensor_alert (jika ada & bukan 58/59) ──
      const alert = (this.alertCodes || []).find((a) => Number(a.alert_code) === num)
      if (alert && alert.sensor_code != null && Number(alert.sensor_code) > 0) {
        const sc = Number(alert.sensor_code)
        if (sc !== 58 && sc !== 59) return sc
      }

      // ── 3. Derive dari media sensor aktif ──
      const allSensors = [...this.sensors, ...this.childSensors]
      for (const s of allSensors) {
        const sc = Number(s.sensor_code)
        if (sc === 58 || sc === 59) continue

        if (s.status_text) {
          const codes = String(s.status_text)
            .split(',')
            .map((v) => v.trim())
          if (codes.includes(String(num))) return sc
        }
        if (s.alert_code_config) {
          let cfg = s.alert_code_config
          if (typeof cfg === 'string') {
            try {
              cfg = JSON.parse(cfg)
            } catch {
              cfg = {}
            }
          }
          if (cfg && typeof cfg === 'object' && cfg[num]) return sc
        }
      }

      return this.getDefaultPhotoSensorCode()
    },

    // ✅ Apakah alert ini profile (ADAS/DMS) — sensor 58/59
    isProfileAlert(alertCode) {
      const num = Number(alertCode)
      const name = this.getAlertCategoryName(num).toUpperCase()
      if (name) {
        if (name.includes('ADAS') || name.includes('ADVANCED DRIVER')) return true
        if (
          name.includes('DMS') ||
          name.includes('DRIVER MONITORING') ||
          name.includes('DRIVER STATE') ||
          name.includes('DRIVER ATTENTION')
        ) {
          return true
        }
      }
      const KNOWN_ADAS_ALERTS = new Set([90, 91, 92, 113, 49, 96])
      const KNOWN_DMS_ALERTS = new Set([51, 53, 112, 54, 55, 110, 52, 111, 56, 50])
      return KNOWN_ADAS_ALERTS.has(num) || KNOWN_DMS_ALERTS.has(num)
    },

    // ✅ Sensor yang di-update via API — selalu media sensor (37/75/40)
    getUpdateSensorCode(cfg, alertCode) {
      if (alertCode != null && this.isProfileAlert(alertCode)) {
        return this.historicalVideoSensorCode
      }
      return cfg.video && this.hasHistoricalVideoSensor
        ? this.historicalVideoSensorCode
        : this.getDefaultPhotoSensorCode()
    },

    getCategoryTypeByName(categoryName) {
      const name = String(categoryName || '').toUpperCase()
      if (!name) return null
      if (name.includes('ADAS') || name.includes('ADVANCED DRIVER')) {
        return this.supportAdas ? 'ADAS' : null
      }
      if (
        name.includes('DMS') ||
        name.includes('DRIVER MONITORING') ||
        name.includes('DRIVER STATE') ||
        name.includes('DRIVER ATTENTION')
      ) {
        return this.supportDms ? 'DMS' : null
      }
      return null
    },

    autoSelectAdasDmsAlerts() {
      if (this.photoSensorError) return
      if (!this.isDataReady) return
      if (!this.availableCameras.length) return
      if (!this.isDeviceCameraReady) return

      const adasDmsAlerts = []
      this.applicableCategories.forEach((cat) => {
        const type = this.getCategoryTypeByName(cat.category_eng)
        if (!type) return
        ;(cat.alert_codes || []).forEach((alert) => {
          const code = Number(alert.alert_code)
          if (!Number.isNaN(code)) adasDmsAlerts.push({ code, type })
        })
      })

      // Skenario 1: hanya sensor 37/75 (foto) → ADAS/DMS tidak auto-check
      if (!this.hasHistoricalVideoSensor) {
        let changed = false
        adasDmsAlerts.forEach(({ code }) => {
          const idx = this.selectedAlertCodes.indexOf(code)
          if (idx >= 0) {
            this.selectedAlertCodes.splice(idx, 1)
            changed = true
          }
        })
        if (changed) this.updateStatusText()
        return
      }

      // Skenario 2: sensor 40 aktif → ADAS/DMS auto-check dengan default channel
      let changed = false
      adasDmsAlerts.forEach(({ code, type }) => {
        const defaultChannel = type === 'ADAS' ? this.adasDefaultChannel : this.dmsDefaultChannel
        if (!defaultChannel) return

        const channelAvailable = this.availableCameras.some(
          (c) => String(c.value) === String(defaultChannel)
        )
        if (!channelAvailable) return

        const naturalCode = this.getNaturalSensorCodeFor(code)

        if (this.selectedAlertCodes.includes(code)) {
          const cfg = this.getSafeCameraConfig(code)
          if (!cfg.cameras.map(String).includes(String(defaultChannel))) {
            this.$set(cfg, 'cameras', [String(defaultChannel), ...cfg.cameras])
            changed = true
          }
          if (!cfg.photo) {
            this.$set(cfg, 'photo', true)
            changed = true
          }
          if (!cfg.video) {
            this.$set(cfg, 'video', true)
            changed = true
          }
          // ✅ FIX: pakai natural sensor (58/59), bukan 40
          if (Number(cfg.sensor_code) !== naturalCode) {
            this.$set(cfg, 'sensor_code', naturalCode)
            changed = true
          }
          return
        }

        this.selectedAlertCodes.push(code)
        const cfg = this.getSafeCameraConfig(code)
        this.$set(cfg, 'cameras', [String(defaultChannel)])
        this.$set(cfg, 'photo', true)
        this.$set(cfg, 'video', true)
        this.$set(cfg, 'sensor_code', naturalCode) // ✅ FIX
        changed = true
      })

      if (changed) this.updateStatusText()
    },

    applyDefaultChannelsToConfig() {
      if (this.photoSensorError) return
      if (!this.form || !this.form.alertCodeConfig) return
      if (!Array.isArray(this.selectedAlertCodes) || this.selectedAlertCodes.length === 0) return
      if (!this.isDeviceCameraReady) return

      this.selectedAlertCodes.forEach((rawCode) => {
        const numCode = Number(rawCode)
        const def = this.getDefaultChannelForAlert(numCode)
        if (def == null) return

        if (!this.form.alertCodeConfig[numCode]) {
          const isProfile = this.isProfileAlert(numCode)
          this.$set(this.form.alertCodeConfig, numCode, {
            cameras: [],
            photo: true,
            video: isProfile, // ✅ ADAS/DMS = video
            sensor_code: this.getNaturalSensorCodeFor(numCode),
          })
        }

        const cfg = this.form.alertCodeConfig[numCode]
        if (!Array.isArray(cfg.cameras)) this.$set(cfg, 'cameras', [])

        const strDef = String(def)
        const already = cfg.cameras.map(String).includes(strDef)
        if (!already) {
          this.$set(cfg, 'cameras', [strDef, ...cfg.cameras])
        }
      })
    },

    async loadInitialData() {
      try {
        await this.getAlertCodes()
        this.dataInitialized = true
      } catch (error) {
        console.error('❌ Failed to load initial data:', error)
        this.dataInitialized = true
      }
    },

    async refreshAlertCodes() {
      this.refreshAttempt++
      this.photoSensorError = null
      this.activePhotoSensorCode = null
      this.isConfigReady = false
      if (this.vehicleId) await this.getSensors()
      await this.fetchDeviceCamera()
      await this.getAlertCodes()
    },

    async fetchDeviceCamera() {
      if (!this.deviceCode && !this.vehicleId) {
        this.deviceCamera = null
        return
      }

      this.isLoadingDeviceCamera = true
      try {
        let res
        if (this.deviceCode) {
          const params = {
            filters: [`device_code eq ${this.deviceCode}`],
          }
          res = await deviceCameraApi.get(params, this.region)
        }

        const list = res?.data?.paginated_result || (Array.isArray(res?.data) ? res.data : []) || []

        this.deviceCamera = this.deviceCode
          ? list.find((d) => String(d.device_code) === String(this.deviceCode)) || null
          : list[0] || null
      } catch (err) {
        console.error('❌ fetchDeviceCamera:', err)
        this.deviceCamera = null
      } finally {
        this.isLoadingDeviceCamera = false
        this.applyDefaultChannelsToConfig()
      }
    },

    isCategoryApplicable(cat) {
      if (!cat) return false
      const name = String(cat.category_eng || '').toUpperCase()

      if (name.includes('ADAS')) {
        if (!this.isDeviceCameraReady) return true
        if (!this.hasHistoricalVideoSensor) return false // ✅ wajib sensor 40
        return this.supportAdas
      }

      if (name.includes('DMS')) {
        if (!this.isDeviceCameraReady) return true
        if (!this.hasHistoricalVideoSensor) return false // ✅ wajib sensor 40
        return this.supportDms
      }

      if (name.includes('DRIVING') || name.includes('BEHAVIOUR') || name.includes('BEHAVIOR')) {
        return true
      }

      return true
    },

    getAlertCategoryType(alertCode) {
      const num = Number(alertCode)
      let categoryName = ''

      const flatAlert = (this.alertCodes || []).find((a) => Number(a.alert_code) === num)
      if (flatAlert && flatAlert.category_eng) {
        categoryName = String(flatAlert.category_eng)
      } else {
        for (const cat of this.alertCategories || []) {
          const found = (cat.alert_codes || []).find((a) => Number(a.alert_code) === num)
          if (found) {
            categoryName = String(cat.category_eng || '')
            break
          }
        }
      }

      return this.getCategoryTypeByName(categoryName)
    },

    getDefaultChannelForAlert(alertCode) {
      const type = this.getAlertCategoryType(alertCode)
      if (type === 'ADAS' && this.supportAdas) return this.adasDefaultChannel
      if (type === 'DMS' && this.supportDms) return this.dmsDefaultChannel
      return null
    },

    getChannelsForAlert(alertCode) {
      const def = this.getDefaultChannelForAlert(alertCode)

      return this.availableCameras.map((c) => ({
        value: String(c.value),
        text: `Kamera ${c.value}`,
        isDefault: def != null && String(c.value) === String(def),
      }))
    },

    isChannelDefault(alertCode, channelValue) {
      const def = this.getDefaultChannelForAlert(alertCode)
      return def != null && String(channelValue) === String(def)
    },

    getAllChannelValuesForAlert(alertCode) {
      return this.getChannelsForAlert(alertCode).map((c) => c.value)
    },

    getSafeCameraConfig(code) {
      const numCode = Number(code)
      const isProfile = this.isProfileAlert(numCode)

      if (!this.form.alertCodeConfig[numCode]) {
        this.$set(this.form.alertCodeConfig, numCode, {
          cameras: [],
          photo: true,
          video: isProfile, // ✅ ADAS/DMS default video
          sensor_code: this.getNaturalSensorCodeFor(numCode),
        })
      }
      const cfg = this.form.alertCodeConfig[numCode]
      if (!Array.isArray(cfg.cameras)) this.$set(cfg, 'cameras', [])
      if (typeof cfg.photo !== 'boolean') this.$set(cfg, 'photo', true)
      if (typeof cfg.video !== 'boolean') this.$set(cfg, 'video', isProfile)

      if (isProfile) {
        // ✅ ADAS/DMS = sensor 40, foto + video WAJIB
        const natural = this.historicalVideoSensorCode
        if (Number(cfg.sensor_code) !== natural) this.$set(cfg, 'sensor_code', natural)
        if (!cfg.photo) this.$set(cfg, 'photo', true)
        if (!cfg.video) this.$set(cfg, 'video', true)
      } else {
        const expected = cfg.video
          ? this.historicalVideoSensorCode
          : this.getDefaultPhotoSensorCode()
        if (Number(cfg.sensor_code) !== expected) {
          this.$set(cfg, 'sensor_code', expected)
        }
      }

      const def = this.getDefaultChannelForAlert(numCode)
      if (def != null && !cfg.cameras.map(String).includes(def)) {
        this.$set(cfg, 'cameras', [def, ...cfg.cameras])
      }
      return cfg
    },

    isAlertSelected(code) {
      return this.selectedAlertCodes.map((c) => Number(c)).includes(Number(code))
    },

    isAlertConfigReady(code) {
      const config = this.form.alertCodeConfig[Number(code)]
      return config && Array.isArray(config.cameras)
    },

    isExpanded(code) {
      return this.expandedAlerts.includes(Number(code))
    },

    toggleExpanded(code) {
      const numCode = Number(code)
      const idx = this.expandedAlerts.indexOf(numCode)
      if (idx >= 0) this.expandedAlerts.splice(idx, 1)
      else this.expandedAlerts.push(numCode)
    },

    isCategoryExpanded(categoryId) {
      return this.expandedCategories.includes(Number(categoryId))
    },

    toggleCategoryExpanded(categoryId) {
      const id = Number(categoryId)
      const idx = this.expandedCategories.indexOf(id)
      if (idx >= 0) this.expandedCategories.splice(idx, 1)
      else this.expandedCategories.push(id)
    },

    expandAllCategories() {
      this.expandedCategories = this.applicableCategories.map((c) => Number(c.alert_category_id))
    },

    collapseAllCategories() {
      this.expandedCategories = []
    },

    getCategoryIcon(cat) {
      const id = Number(cat.alert_category_id)
      const map = {
        1: 'fa-flag',
        2: 'fa-user-tag',
        3: 'fa-user-shield',
        4: 'fa-shield-alt',
        7: 'fa-satellite-dish',
      }
      return map[id] || 'fa-layer-group'
    },

    getCategorySelectedCount(cat) {
      if (!cat || !cat.alert_codes) return 0
      const selected = new Set(this.selectedAlertCodes.map((c) => Number(c)))
      return cat.alert_codes.filter((a) => selected.has(Number(a.alert_code))).length
    },

    isCategoryFullySelected(cat) {
      if (!cat || !cat.alert_codes || cat.alert_codes.length === 0) return false
      const selected = new Set(this.selectedAlertCodes.map((c) => Number(c)))
      return cat.alert_codes.every((a) => selected.has(Number(a.alert_code)))
    },

    isCategoryPartiallySelected(cat) {
      if (!cat || !cat.alert_codes || cat.alert_codes.length === 0) return false
      const selected = new Set(this.selectedAlertCodes.map((c) => Number(c)))
      const count = cat.alert_codes.filter((a) => selected.has(Number(a.alert_code))).length
      return count > 0 && count < cat.alert_codes.length
    },

    toggleCategorySelection(cat) {
      if (!cat || !cat.alert_codes) return
      const isFullySelected = this.isCategoryFullySelected(cat)

      if (isFullySelected) {
        cat.alert_codes.forEach((a) => {
          const numCode = Number(a.alert_code)
          const idx = this.selectedAlertCodes.indexOf(numCode)
          if (idx >= 0) this.selectedAlertCodes.splice(idx, 1)
          const expIdx = this.expandedAlerts.indexOf(numCode)
          if (expIdx >= 0) this.expandedAlerts.splice(expIdx, 1)
        })
      } else {
        cat.alert_codes.forEach((a) => {
          const numCode = Number(a.alert_code)
          const isProfile = this.isProfileAlert(numCode)
          if (!this.selectedAlertCodes.includes(numCode)) {
            this.selectedAlertCodes.push(numCode)
          }
          if (!this.expandedAlerts.includes(numCode)) {
            this.expandedAlerts.push(numCode)
          }
          const cfg = this.getSafeCameraConfig(numCode)
          this.$set(cfg, 'cameras', this.getAllChannelValuesForAlert(numCode))
          this.$set(cfg, 'photo', true)
          this.$set(cfg, 'video', isProfile) // ✅
          this.$set(cfg, 'sensor_code', this.getNaturalSensorCodeFor(numCode))
        })
      }

      this.updateStatusText()
      this.trackChanges()
    },

    toggleAlert(alertCode) {
      const numCode = Number(alertCode)
      const idx = this.selectedAlertCodes.indexOf(numCode)
      const isProfile = this.isProfileAlert(numCode)

      if (idx >= 0) {
        this.selectedAlertCodes.splice(idx, 1)
        const expIdx = this.expandedAlerts.indexOf(numCode)
        if (expIdx >= 0) this.expandedAlerts.splice(expIdx, 1)
      } else {
        this.selectedAlertCodes.push(numCode)
        if (!this.expandedAlerts.includes(numCode)) {
          this.expandedAlerts.push(numCode)
        }
        const cfg = this.getSafeCameraConfig(numCode)
        this.$set(cfg, 'cameras', this.getAllChannelValuesForAlert(numCode))
        this.$set(cfg, 'photo', true)
        this.$set(cfg, 'video', isProfile) // ✅ profile → true
        this.$set(cfg, 'sensor_code', this.getNaturalSensorCodeFor(numCode))
      }

      this.handleAlertCodeChange(numCode)
    },

    toggleCamera(alertCode, camValue) {
      if (this.isChannelDefault(alertCode, camValue)) return

      const cfg = this.getSafeCameraConfig(alertCode)
      const idx = cfg.cameras.map(String).indexOf(String(camValue))
      if (idx >= 0) cfg.cameras.splice(idx, 1)
      else cfg.cameras.push(camValue)
      this.trackChanges()
    },

    isCameraChecked(alertCode, camValue) {
      if (this.isChannelDefault(alertCode, camValue)) return true

      const cfg = this.form.alertCodeConfig[Number(alertCode)]
      return !!(
        cfg &&
        Array.isArray(cfg.cameras) &&
        cfg.cameras.map(String).includes(String(camValue))
      )
    },

    isMediaActive(alertCode, type) {
      const cfg = this.form.alertCodeConfig[Number(alertCode)]
      if (!cfg) return type === 'photo'
      return type === 'photo' ? !!cfg.photo : !!cfg.video
    },

    setMedia(alertCode, type) {
      const numCode = Number(alertCode)

      if (this.isProfileAlert(numCode)) {
        // ✅ ADAS/DMS: selalu Foto + Video, sensor 40, tidak bisa diubah
        const cfg = this.getSafeCameraConfig(numCode)
        this.$set(cfg, 'photo', true)
        this.$set(cfg, 'video', true)
        this.$set(cfg, 'sensor_code', this.historicalVideoSensorCode)
        this.trackChanges()
        return
      }

      const cfg = this.getSafeCameraConfig(numCode)

      if (type === 'video' && !this.hasHistoricalVideoSensor) {
        console.warn('⚠️ Video media type is only available when sensor 40 is active')
        return
      }

      if (type === 'photo') {
        if (cfg.video) this.$set(cfg, 'video', false)
        this.$set(cfg, 'photo', true)
      } else if (type === 'video') {
        const nextVideo = !cfg.video
        this.$set(cfg, 'video', nextVideo)
        if (nextVideo) this.$set(cfg, 'photo', true)
      }
      this.$set(
        cfg,
        'sensor_code',
        cfg.video ? this.historicalVideoSensorCode : this.getDefaultPhotoSensorCode()
      )
      this.trackChanges()
    },

    getMediaLabel(alertCode) {
      const cfg = this.form.alertCodeConfig[Number(alertCode)]
      if (!cfg) return 'Foto'
      if (cfg.video && cfg.photo) return 'Foto + Video'
      return 'Foto'
    },

    getSensorCodeFor(alertCode) {
      const num = Number(alertCode)

      // ✅ ADAS/DMS selalu tampil sensor 40
      if (this.isProfileAlert(num)) {
        return this.historicalVideoSensorCode
      }

      const cfg = this.form.alertCodeConfig[num]
      if (!cfg) return this.getDefaultPhotoSensorCode()
      return cfg.video
        ? this.historicalVideoSensorCode
        : cfg.sensor_code || this.getDefaultPhotoSensorCode()
    },

    selectAllCamerasFor(alertCode) {
      const cfg = this.getSafeCameraConfig(alertCode)
      this.$set(cfg, 'cameras', this.getAllChannelValuesForAlert(alertCode))
      this.trackChanges()
    },

    clearCamerasFor(alertCode) {
      const cfg = this.getSafeCameraConfig(alertCode)
      const def = this.getDefaultChannelForAlert(alertCode)
      this.$set(cfg, 'cameras', def != null ? [def] : [])
      this.trackChanges()
    },

    getCameraConfig(code) {
      return this.ensureAlertCodeConfig(code)
    },

    ensureAlertCodeConfig(code) {
      const numCode = Number(code)
      const isProfile = this.isProfileAlert(numCode)

      if (!this.form.alertCodeConfig || typeof this.form.alertCodeConfig !== 'object') {
        this.$set(this.form, 'alertCodeConfig', {})
      }
      if (!this.form.alertCodeConfig[numCode]) {
        this.$set(this.form.alertCodeConfig, numCode, {
          cameras: [],
          photo: true,
          video: isProfile,
          sensor_code: this.getNaturalSensorCodeFor(numCode),
        })
      }
      const cfg = this.form.alertCodeConfig[numCode]
      if (!Array.isArray(cfg.cameras)) this.$set(cfg, 'cameras', [])
      if (typeof cfg.photo !== 'boolean') this.$set(cfg, 'photo', true)
      if (typeof cfg.video !== 'boolean') this.$set(cfg, 'video', isProfile)

      if (isProfile) {
        const natural = this.historicalVideoSensorCode
        if (Number(cfg.sensor_code) !== natural) this.$set(cfg, 'sensor_code', natural)
        if (!cfg.video) this.$set(cfg, 'video', true)
        if (!cfg.photo) this.$set(cfg, 'photo', true)
      } else {
        const expected = cfg.video
          ? this.historicalVideoSensorCode
          : this.getDefaultPhotoSensorCode()
        if (Number(cfg.sensor_code) !== expected) {
          this.$set(cfg, 'sensor_code', expected)
        }
      }
      return cfg
    },

    handleOk(bvModalEvent) {
      bvModalEvent.preventDefault()
    },

    isCameraValid() {
      if (!this.selectedAlertCodes || this.selectedAlertCodes.length === 0) return true
      return this.selectedAlertCodes.every((code) => {
        const def = this.getDefaultChannelForAlert(code)
        const cfg = this.form.alertCodeConfig[Number(code)]
        if (!cfg || !Array.isArray(cfg.cameras)) return false
        if (def != null && cfg.cameras.map(String).includes(def)) return true
        return cfg.cameras.length > 0
      })
    },

    async initModalState() {
      if (this.photoSensorError) return

      this.hasUserMadeChanges = false

      if (!this.isDataReady) {
        return this.$nextTick().then(() => {
          if (!this.isDataReady && !this.photoSensorError) {
            return new Promise((resolve) => {
              const checkInterval = setInterval(() => {
                if (this.isDataReady || this.photoSensorError) {
                  clearInterval(checkInterval)
                  if (this.isDataReady) this.initModalState()
                  resolve()
                }
              }, 100)
              setTimeout(() => {
                clearInterval(checkInterval)
                resolve()
              }, 5000)
            })
          }
        })
      }

      if (!this.alertCodes || this.alertCodes.length === 0) return

      this.selectedAlertCodes = this.selectedAlertCodes.filter((code) =>
        this.alertCodes.some((a) => Number(a.alert_code) === Number(code))
      )

      this.autoSelectAdasDmsAlerts()
      this.syncAdasDmsMediaType()

      this.expandedAlerts = [...this.selectedAlertCodes]

      const catIdsWithSelected = new Set()
      this.applicableCategories.forEach((cat) => {
        const hasSelected = (cat.alert_codes || []).some((a) =>
          this.selectedAlertCodes.includes(Number(a.alert_code))
        )
        if (hasSelected) catIdsWithSelected.add(Number(cat.alert_category_id))
      })
      this.expandedCategories = Array.from(catIdsWithSelected)

      this.selectedAlertCodes.forEach((code) => {
        const numCode = Number(code)
        if (this.isProfileAlert(numCode)) {
          const cfg = this.getSafeCameraConfig(numCode)
          if (Number(cfg.sensor_code) !== this.historicalVideoSensorCode) {
            this.$set(cfg, 'sensor_code', this.historicalVideoSensorCode)
          }
          if (!cfg.video) this.$set(cfg, 'video', true)
          if (!cfg.photo) this.$set(cfg, 'photo', true)
        }
      })

      this.applyDefaultChannelsToConfig()

      if (!Array.isArray(this.selectedAlertCodes)) this.selectedAlertCodes = []
      this.updateStatusText()

      this.hasUserMadeChanges = false
      this.isDisabledSaveButton = true
    },

    syncAdasDmsMediaType() {
      if (this.photoSensorError) return
      if (!this.hasHistoricalVideoSensor) return
      if (!Array.isArray(this.selectedAlertCodes) || this.selectedAlertCodes.length === 0) return

      const selected = new Set(this.selectedAlertCodes.map((c) => Number(c)))

      this.applicableCategories.forEach((cat) => {
        const type = this.getCategoryTypeByName(cat.category_eng)
        if (!type) return
        ;(cat.alert_codes || []).forEach((alert) => {
          const code = Number(alert.alert_code)
          if (!selected.has(code)) return

          const cfg = this.getSafeCameraConfig(code)
          if (!cfg.photo) this.$set(cfg, 'photo', true)
          if (!cfg.video) this.$set(cfg, 'video', true)
          // ✅ ADAS/DMS → sensor 40
          if (Number(cfg.sensor_code) !== this.historicalVideoSensorCode) {
            this.$set(cfg, 'sensor_code', this.historicalVideoSensorCode)
          }
        })
      })
    },

    async handleSubmit(bvModalEvent) {
      bvModalEvent.preventDefault()

      if (this.photoSensorError) {
        this.$toast.error(this.photoSensorErrorMessage || 'Konfigurasi sensor tidak valid')
        return
      }

      if (!this.isCameraValid()) {
        this.$toast.error('Semua alert yang dipilih harus memiliki minimal 1 kamera')
        this.invalidAlerts.forEach((code) => {
          if (!this.expandedAlerts.includes(Number(code))) {
            this.expandedAlerts.push(Number(code))
          }
        })
        this.applicableCategories.forEach((cat) => {
          if (
            (cat.alert_codes || []).some((a) => this.invalidAlerts.includes(Number(a.alert_code)))
          ) {
            if (!this.expandedCategories.includes(Number(cat.alert_category_id))) {
              this.expandedCategories.push(Number(cat.alert_category_id))
            }
          }
        })
        return
      }

      this.loading = true
      this.isDisabled = true
      try {
        const applicableCodes = new Set(
          this.applicableCategories.flatMap((c) =>
            (c.alert_codes || []).map((a) => Number(a.alert_code))
          )
        )

        const configToSend = {}
        this.selectedAlertCodes
          .filter((code) => applicableCodes.has(Number(code)))
          .forEach((code) => {
            const numCode = Number(code)
            const cfg = this.form.alertCodeConfig[numCode]
            if (!cfg || !Array.isArray(cfg.cameras) || cfg.cameras.length === 0) return

            const def = this.getDefaultChannelForAlert(numCode)
            const camerasToSave =
              def != null ? cfg.cameras.filter((c) => String(c) !== def) : cfg.cameras

            if (camerasToSave.length === 0) return

            const finalCfg = { ...cfg, cameras: camerasToSave }

            if (this.isProfileAlert(numCode)) {
              // ✅ ADAS/DMS → sensor 40, foto + video wajib
              finalCfg.sensor_code = this.historicalVideoSensorCode
              finalCfg.photo = true
              finalCfg.video = true
            } else if (typeof finalCfg.sensor_code !== 'number') {
              finalCfg.sensor_code = finalCfg.video
                ? this.historicalVideoSensorCode
                : this.getDefaultPhotoSensorCode()
            }

            configToSend[numCode] = finalCfg
          })

        let sensorsToUpdate = []
        if (Object.keys(configToSend).length === 0) {
          const relevantSensorCodes = [
            this.picturesSensorCode,
            this.oneSecondVideoSensorCode,
            this.historicalVideoSensorCode,
          ]

          sensorsToUpdate = this.sensors
            .filter((s) => relevantSensorCodes.includes(Number(s.sensor_code)))
            .map((s) => {
              const pinCode = s.pin_code
              if (pinCode === null || pinCode === undefined || typeof pinCode !== 'number') {
                return null
              }
              return {
                sensorCode: Number(s.sensor_code),
                pinCode,
                configs: {},
                alertCodes: [],
              }
            })
            .filter((s) => s !== null)

          if (sensorsToUpdate.length === 0) {
            this.$toast.warning('Tidak ada sensor gambar yang tersedia untuk direset')
            this.loading = false
            this.isDisabled = false
            return
          }
        } else {
          // ✅ FIX: Routing ke MEDIA sensor (37/75/40), BUKAN ke cfg.sensor_code (58/59).
          // Config tetap bawa sensor_code: 58/59 untuk alert ADAS/DMS.
          const grouped = {}
          Object.keys(configToSend).forEach((code) => {
            const numCode = Number(code)
            const cfg = configToSend[code]

            const updateSensorCode = this.getUpdateSensorCode(cfg, numCode)

            if (!grouped[updateSensorCode]) {
              grouped[updateSensorCode] = { alertCodes: [], configs: {} }
            }
            grouped[updateSensorCode].alertCodes.push(numCode)
            grouped[updateSensorCode].configs[code] = cfg
          })

          sensorsToUpdate = Object.keys(grouped)
            .map((sensorCode) => {
              const group = grouped[sensorCode]
              const sensor = this.sensors.find((s) => Number(s.sensor_code) === Number(sensorCode))
              const pinCode = sensor ? sensor.pin_code : this.pinCode
              if (pinCode === null || pinCode === undefined || typeof pinCode !== 'number') {
                return null
              }
              return {
                sensorCode: Number(sensorCode),
                pinCode,
                configs: group.configs,
                alertCodes: group.alertCodes,
              }
            })
            .filter((s) => s !== null)

          if (sensorsToUpdate.length === 0) {
            this.$toast.warning('Tidak ada sensor dengan pin_code valid yang dapat diperbarui')
            this.loading = false
            this.isDisabled = false
            return
          }
        }

        const updatePromises = sensorsToUpdate.map(
          ({ sensorCode, pinCode, configs, alertCodes }) => {
            const statusText = alertCodes && alertCodes.length ? alertCodes.join(',') : ''
            const payload = {
              status_text: statusText,
              alert_code_config: configs,
            }
            return sensorApi.update(this.vehicleId, sensorCode, pinCode, payload, this.region)
          }
        )

        const responses = await Promise.all(updatePromises)
        if (responses.some((r) => !r?.data)) {
          throw new Error('Salah satu update gagal')
        }

        this.$toast.success(this.$t('global.success.save'))
        await this.getSensors()
        this.hasUserMadeChanges = false
        this.isDisabledSaveButton = true
        this.$bvModal.hide('modal-form')
      } catch (error) {
        console.error('❌ Submit error:', error)
        this.handleError(error)
      } finally {
        this.loading = false
        this.isDisabled = false
        this.isDisabledSaveButton = true
      }
    },

    async getSensors() {
      this.isLoadingSensors = true
      this.photoSensorError = null
      this.activePhotoSensorCode = null
      try {
        const res = await masterSensorApi.getByVehicleId(this.vehicleId, this.region)
        this.sensors = Array.isArray(res?.sensor_type) ? res.sensor_type : []
        this.childSensors = Array.isArray(res?.sensor_type) ? res.sensor_type : []

        if (this.childVehicleId && Number(this.vehicleId) !== Number(this.childVehicleId)) {
          const res2 = await masterSensorApi.getByVehicleId(this.childVehicleId, this.region)
          this.childSensors = Array.isArray(res2?.sensor_type) ? res2.sensor_type : []
        }

        const cameraSensor = this.childSensors.find(
          (s) => Number(s.sensor_code) === this.cameraListSensorCode
        )
        this.availableCameras = []
        if (cameraSensor?.status_text) {
          const cameras = [
            ...new Set(
              String(cameraSensor.status_text)
                .split(',')
                .map((v) => v.trim())
                .filter(Boolean)
            ),
          ]
          this.availableCameras = cameras
            .sort((a, b) => Number(a) - Number(b))
            .map((val) => ({ value: val, text: `Kamera ${val}` }))
        }

        const parseCameras = (rawCam) => {
          if (!rawCam) return []
          if (Array.isArray(rawCam)) {
            return rawCam.map((v) => String(v).trim()).filter(Boolean)
          }
          if (typeof rawCam === 'string') {
            return rawCam
              .split(',')
              .map((v) => v.trim())
              .filter(Boolean)
          }
          if (typeof rawCam === 'number') {
            return [String(rawCam)]
          }
          return []
        }

        const parseConfig = (sensor) => {
          if (!sensor || !sensor.alert_code_config) return {}
          let raw = sensor.alert_code_config
          if (typeof raw === 'string') {
            try {
              raw = JSON.parse(raw)
            } catch {
              raw = {}
            }
          }
          if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
          return raw
        }

        const sensor37 = this.sensors.find((s) => Number(s.sensor_code) === this.picturesSensorCode)
        const sensor75 = this.sensors.find(
          (s) => Number(s.sensor_code) === this.oneSecondVideoSensorCode
        )
        const sensor40 = this.sensors.find(
          (s) => Number(s.sensor_code) === this.historicalVideoSensorCode
        )

        const config37 = parseConfig(sensor37)
        const config75 = parseConfig(sensor75)
        const config40 = parseConfig(sensor40)

        const sensor37Exists = !!sensor37
        const sensor75Exists = !!sensor75

        if (sensor37Exists && sensor75Exists) {
          this.photoSensorError = 'BOTH_ACTIVE'
          this.activePhotoSensorCode = null
          this.isConfigReady = false
          this.isLoadingSensors = false
          return
        }

        if (!sensor37Exists && !sensor75Exists) {
          this.photoSensorError = 'NONE_ACTIVE'
          this.activePhotoSensorCode = null
          this.isConfigReady = false
          this.isLoadingSensors = false
          return
        }

        this.activePhotoSensorCode = sensor37Exists
          ? this.picturesSensorCode
          : this.oneSecondVideoSensorCode
        this.photoSensorError = null

        const getCamerasForCode = (numCode) => {
          if (config37 && config37[numCode]) return parseCameras(config37[numCode].cameras)
          if (config75 && config75[numCode]) return parseCameras(config75[numCode].cameras)
          if (config40 && config40[numCode]) return parseCameras(config40[numCode].cameras)
          return []
        }

        const status37 = Object.keys(config37)
        const status75 = Object.keys(config75)
        const status40 = Object.keys(config40)

        const defaultPhotoCode = this.activePhotoSensorCode
        const allActiveCodes = new Set([...status37, ...status75, ...status40])

        const codesWithCameras = Array.from(allActiveCodes).filter((code) => {
          return getCamerasForCode(Number(code)).length > 0
        })

        const mergedConfig = {}
        for (const code of codesWithCameras) {
          const numCode = Number(code)
          let cameras = []
          let sourcePhotoCode = defaultPhotoCode
          let originalCfg = null

          if (config37 && config37[numCode]) {
            cameras = parseCameras(config37[numCode].cameras)
            sourcePhotoCode = this.picturesSensorCode
            originalCfg = config37[numCode]
          } else if (config75 && config75[numCode]) {
            cameras = parseCameras(config75[numCode].cameras)
            sourcePhotoCode = this.oneSecondVideoSensorCode
            originalCfg = config75[numCode]
          } else if (config40 && config40[numCode]) {
            cameras = parseCameras(config40[numCode].cameras)
            originalCfg = config40[numCode]
          }

          const hasVideoConfig = !!(
            config40 &&
            config40[numCode] &&
            Array.isArray(config40[numCode].cameras) &&
            config40[numCode].cameras.length > 0
          )
          const isVideo = hasVideoConfig

          // ✅ FIX: preserve existing sensor_code (58/59) if present
          const naturalSensor = this.getNaturalSensorCodeFor(numCode)
          const isProfile = naturalSensor === 58 || naturalSensor === 59

          const sensorCodeValue = isProfile
            ? naturalSensor
            : typeof originalCfg?.sensor_code === 'number' && originalCfg.sensor_code > 0
            ? originalCfg.sensor_code
            : isVideo
            ? this.historicalVideoSensorCode
            : sourcePhotoCode

          mergedConfig[numCode] = {
            cameras,
            photo: true,
            video: isVideo,
            sensor_code: sensorCodeValue,
          }
        }

        this.$set(this.form, 'alertCodeConfig', mergedConfig)

        this.selectedAlertCodes = codesWithCameras.map(Number).sort((a, b) => a - b)
        this.expandedAlerts = [...this.selectedAlertCodes]

        this.isConfigReady = true

        this.selectedAlertCodes.forEach((code) => {
          this.getSafeCameraConfig(code)
        })

        await this.$nextTick()

        this.applyDefaultChannelsToConfig()

        this.updateStatusText()
        this.isDisabled = false
      } catch (err) {
        console.error('❌ getSensors error:', err)
        this.$toast.error(err?.message || 'Failed to fetch sensors')
      } finally {
        this.isLoadingSensors = false
      }
    },

    getAlertCategoryName(alertCode) {
      const num = Number(alertCode)

      const flatAlert = (this.alertCodes || []).find((a) => Number(a.alert_code) === num)
      if (flatAlert && flatAlert.category_eng) return String(flatAlert.category_eng)

      for (const cat of this.alertCategories || []) {
        const found = (cat.alert_codes || []).find((a) => Number(a.alert_code) === num)
        if (found) return String(cat.category_eng || '')
      }
      return ''
    },

    async getAlertCodes() {
      this.isLoadingAlertCodes = true
      try {
        this.alertCategories = []
        this.alertCodes = []

        if (!this.isMultiImei && !this.deviceCode) {
          this.isLoadingAlertCodes = false
          return
        }

        let params = {
          page: 1,
          limit: 1,
        }

        params.filters = [`vehicle_id eq ${this.vehicleId}`]

        let response = await sensorAlertApi.get(params, this.region)

        const total = response?.metadata?.total_count || 0
        if (response.status && total > 1) {
          params.limit = total
          response = await sensorAlertApi.get(params, this.region)
        }

        const categories = response?.data?.paginated_result || []

        const normalized = []
        const flat = []

        categories.forEach((cat) => {
          const catId = Number(cat.alert_category_id)
          const alerts = (cat.alert_codes || []).map((a) => {
            // ✅ FIX: capture sensor_code natural dari API
            const normalizedAlert = {
              ...a,
              alert_code: Number(a.alert_code),
              sensor_code: a.sensor_code != null ? Number(a.sensor_code) : null,
              category_id: catId,
              category_eng: cat.category_eng,
              category_idn: cat.category_idn,
            }
            flat.push(normalizedAlert)
            return normalizedAlert
          })

          normalized.push({
            alert_category_id: catId,
            category_eng: cat.category_eng,
            category_idn: cat.category_idn,
            description_eng: cat.description_eng,
            description_idn: cat.description_idn,
            icon_url: cat.icon_url,
            alert_codes: alerts,
          })
        })

        this.alertCategories = normalized
        this.alertCodes = flat
      } catch (err) {
        console.error('❌ getAlertCodes error:', err)
        this.handleError(err)
        this.isDisabledSaveButton = true
        this.resetForm()
        this.isLoadingAlertCodes = false
        return
      }

      if (this.alertCategories.length === 0 && this.selectedAlertCodes.length > 0) {
        this.alertCodes = this.selectedAlertCodes.map((code) => ({
          alert_code: Number(code),
          description_eng: `Alert ${code}`,
        }))
      }
      this.isLoadingAlertCodes = false

      this.applyDefaultChannelsToConfig()
    },

    handleError(error) {
      if (error.response) {
        this.$toast.error(
          error.response.data?.error || error.response.data?.message || 'Terjadi kesalahan'
        )
      } else {
        this.$toast.error(error.message || 'Terjadi kesalahan')
      }
    },

    handleAlertCodeChange(alertCode) {
      const numCode = Number(alertCode)
      this.ensureAlertCodeConfig(numCode)
      this.selectedAlertCodes.forEach((code) => {
        const n = Number(code)
        if (!this.form.alertCodeConfig[n]) {
          this.$set(this.form.alertCodeConfig, n, { cameras: [] })
        }
      })
      if (!this.selectedAlertCodes.map((c) => Number(c)).includes(numCode)) {
        this.$delete(this.form.alertCodeConfig, numCode)
      }
      this.updateStatusText()
      this.trackChanges()
    },

    updateStatusText() {
      this.form.status_text = this.selectedAlertCodes.map((v) => String(v)).join(',')
    },

    trackChanges() {
      this.hasUserMadeChanges = true
      this.isDisabledSaveButton = false
    },

    resetForm() {
      this.form = { status_text: null, alertCodeConfig: {} }
      this.selectedAlertCodes = []
      this.searchQuery = ''
      this.originalForm = JSON.parse(JSON.stringify(this.form))
      this.modifiedFields = {}
      this.hasUserMadeChanges = false
      this.isDisabledSaveButton = true
      this.isDisabled = true
      this.alertCodes = []
      this.alertCategories = []
      this.expandedCategories = []
    },

    selectAll() {
      if (this.alertCodes.length === 0) return

      const applicableCodes = new Set(
        this.applicableCategories.flatMap((c) =>
          (c.alert_codes || []).map((a) => Number(a.alert_code))
        )
      )

      const newSelectedCodes = []
      const newAlertCodeConfig = { ...this.form.alertCodeConfig }

      this.alertCodes
        .filter((alert) => applicableCodes.has(Number(alert.alert_code)))
        .forEach((alert) => {
          const code = Number(alert.alert_code)
          const isProfile = this.isProfileAlert(code)
          newSelectedCodes.push(code)

          let existingConfig = newAlertCodeConfig[code]
          if (!existingConfig) {
            const originalConfig = this.originalForm.alertCodeConfig?.[code]
            if (originalConfig) {
              existingConfig = JSON.parse(JSON.stringify(originalConfig))
            } else {
              existingConfig = {
                cameras: [],
                photo: true,
                video: isProfile,
                sensor_code: this.getNaturalSensorCodeFor(code),
              }
            }
          }

          existingConfig = {
            cameras: existingConfig.cameras || [],
            photo: typeof existingConfig.photo === 'boolean' ? existingConfig.photo : true,
            video: isProfile
              ? true
              : typeof existingConfig.video === 'boolean'
              ? existingConfig.video
              : false, // ✅
            sensor_code: this.getNaturalSensorCodeFor(code),
          }

          existingConfig.cameras = this.getAllChannelValuesForAlert(code)
          newAlertCodeConfig[code] = existingConfig
        })

      this.selectedAlertCodes = newSelectedCodes
      this.$set(this.form, 'alertCodeConfig', newAlertCodeConfig)
      this.expandedAlerts = [...newSelectedCodes]

      this.expandedCategories = this.applicableCategories.map((c) => Number(c.alert_category_id))

      this.applyDefaultChannelsToConfig()

      this.updateStatusText()
      this.trackChanges()
    },

    deselectAll() {
      this.selectedAlertCodes = []
      this.$set(this.form, 'alertCodeConfig', {})
      this.expandedAlerts = []
      this.updateStatusText()
      this.trackChanges()
    },

    expandAllSelected() {
      this.expandedAlerts = [...this.selectedAlertCodes.map((c) => Number(c))]
    },

    collapseAll() {
      this.expandedAlerts = []
    },

    getCameraLabel(alertCode) {
      const cfg = this.form.alertCodeConfig[Number(alertCode)]
      if (!cfg || !cfg.cameras || cfg.cameras.length === 0) return ''
      return cfg.cameras.join(', ')
    },

    getDefaultCamerasForAlert(alertCode) {
      const def = this.getDefaultChannelForAlert(alertCode)
      if (def != null) return [String(def)]
      if (this.availableCameras && this.availableCameras.length > 0) {
        return [String(this.availableCameras[0].value)]
      }
      return []
    },
  },
}
</script>

<style scoped>
/* ============ HEADER STYLE (SAMA PERSIS SS) ============ */
.dic-header {
  background-color: #1e40af;
  border-bottom: none;
}

.dic-header-text .modal-title {
  font-size: 1.15rem;
  letter-spacing: 0.2px;
}

.dic-header-text small {
  font-size: 0.75rem;
}

/* ============ TOKENS ============ */
.alert-config-modal >>> .modal-content,
.alert-config-modal /deep/ .modal-content {
  border-radius: 12px;
  overflow: hidden;
  border: none;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.15);
}

/* ============ LOADING / ERROR ============ */
.dic-loading {
  padding: 3.5rem 1rem;
  text-align: center;
  color: #64748b;
  background: #fff;
}

.dic-error {
  padding: 3rem 1.5rem;
  text-align: center;
  color: #64748b;
  max-width: 560px;
  margin: 0 auto;
  background: #fff;
}

.dic-error .text-warning {
  color: #f59e0b !important;
}

.fw-500 {
  font-weight: 500;
}
.fw-600 {
  font-weight: 600;
}
.fs-5 {
  font-size: 1.125rem;
}

/* ============ BODY ============ */
.dic-body {
  background: #f8fafc;
}

.dic-info {
  display: flex;
  gap: 0.75rem;
  padding: 0.85rem 1.25rem;
  background: #eff6ff;
  color: #1e40af;
  border-bottom: 1px solid #dbeafe;
  font-size: 0.875rem;
}

.dic-info__icon {
  flex: 0 0 auto;
  padding-top: 2px;
}
.dic-info__text {
  flex: 1;
  line-height: 1.4;
}

/* ============ TOOLBAR ============ */
.dic-toolbar {
  position: sticky;
  top: 0;
  background: linear-gradient(180deg, #fafbfc 0%, #f5f7fa 100%);
  border-bottom: 1px solid #e2e8f0;
  padding: 1rem 1.25rem;
  z-index: 10;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
}

.dic-toolbar__row {
  display: flex;
  align-items: center;
  gap: 1.2rem;
  margin-bottom: 1rem;
}

.dic-toolbar__row:last-child {
  margin-bottom: 0;
}
.dic-toolbar__row--between {
  justify-content: space-between;
}

/* SEARCH */
.dic-search {
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
}

.dic-search__icon {
  position: absolute;
  left: 0.9rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 1rem;
  pointer-events: none;
  z-index: 2;
}

.dic-search__input {
  width: 100%;
  padding: 0.65rem 2.6rem 0.65rem 2.5rem !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 10px !important;
  background: linear-gradient(135deg, #fafbfc 0%, #f5f7fa 100%) !important;
  font-size: 0.875rem;
}

.dic-search__input:focus {
  border-color: #0d6efd !important;
  background: #fff !important;
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.12) !important;
  outline: none !important;
}

.dic-search__clear {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid transparent;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.35rem;
  width: 1.8rem;
  height: 1.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}

/* FILTER */
.dic-filter-wrapper {
  flex: 0 0 auto;
  margin-left: 1.5rem;
  display: flex;
  align-items: center;
}

.dic-filter-input {
  display: none;
}

.dic-filter-label {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.5rem 0.9rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  user-select: none;
}

.dic-filter-input:checked + .dic-filter-label {
  background: #eff6ff;
  border-color: #0d6efd;
  color: #0d6efd;
}

.dic-filter-checkmark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.1rem;
  height: 1.1rem;
  border: 1.5px solid #cbd5e1;
  border-radius: 5px;
  background: #fff;
}

.dic-filter-input:checked + .dic-filter-label .dic-filter-checkmark {
  background: #0d6efd;
  border-color: #0d6efd;
  box-shadow: inset 0 0 0 2px #fff;
}

.dic-filter-input:checked + .dic-filter-label .dic-filter-checkmark::after {
  content: '✓';
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
}

/* SUMMARY + ACTIONS */
.dic-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.8rem;
}

.dic-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.4rem;
  height: 1.95rem;
  padding: 0 0.75rem;
  background: linear-gradient(135deg, #0d6efd 0%, #0a5fd7 100%);
  color: #fff;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
}

.dic-toolbar__actions {
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
  align-items: center;
}

/* ============ LIST ============ */
.dic-list {
  flex: 1;
  overflow-y: auto;
  max-height: 550px;
  padding: 0.75rem 1.25rem;
}

.dic-empty {
  padding: 2rem 1.5rem;
  text-align: center;
  color: #64748b;
}

.dic-empty--full {
  padding: 4rem 1.5rem;
}

/* ============ CATEGORY ============ */
.dic-category {
  margin-bottom: 0.9rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
}

.dic-category__header {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  background: linear-gradient(135deg, #f0f7ff 0%, #e6f1ff 100%);
  border-bottom: 1px solid #dbeafe;
  user-select: none;
}

.dic-category__header:hover {
  background: linear-gradient(135deg, #e6f1ff 0%, #dbeafe 100%);
}

.dic-category__check {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
}

.dic-category__icon {
  flex: 0 0 auto;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #0d6efd;
  color: #fff;
  font-size: 0.9rem;
}

.dic-category__title {
  flex: 1;
  min-width: 0;
}

.dic-category__name-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.dic-category__name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e3a8a;
  letter-spacing: -0.2px;
}

.dic-category__badge {
  display: inline-flex;
  align-items: center;
  height: 1.15rem;
  padding: 0 0.5rem;
  background: #fff;
  color: #0d6efd;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
}

.dic-category__subtitle {
  font-size: 0.75rem;
  color: #64748b;
  margin-top: 2px;
}

.dic-category__toggle {
  flex: 0 0 auto;
  width: 1.9rem;
  height: 1.9rem;
  padding: 0;
  border: 1px solid #bfdbfe;
  border-radius: 7px;
  background: #fff;
  color: #1d4ed8;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dic-category__toggle:hover {
  background: #eff6ff;
}

.dic-category__body {
  padding: 0.75rem;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

/* ============ CARD ============ */
.dic-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
}

.dic-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.08);
}

.dic-card.is-selected {
  border-color: #0d6efd;
  background: linear-gradient(135deg, #fbfdff 0%, #f0f7ff 100%);
  box-shadow: 0 0 0 1px rgba(13, 110, 253, 0.2), 0 2px 8px rgba(13, 110, 253, 0.1);
}

.dic-card.is-invalid {
  border-color: #fca5a5;
  background: linear-gradient(135deg, #fef2f2 0%, #ffe5e5 100%);
}

.dic-card__header {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.85rem 1rem;
  cursor: pointer;
  user-select: none;
}

.dic-card__check {
  flex: 0 0 auto;
}

.dic-card__checkbox {
  appearance: none;
  -webkit-appearance: none;
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dic-card__checkbox:checked {
  background: #0d6efd;
  border-color: #0d6efd;
}

.dic-card__checkbox:checked::after {
  content: '✓';
  color: #fff;
  font-size: 0.9rem;
  font-weight: bold;
  position: absolute;
}

.dic-card__checkbox:indeterminate {
  background: #0d6efd;
  border-color: #0d6efd;
}

.dic-card__checkbox:indeterminate::after {
  content: '—';
  color: #fff;
  font-size: 0.75rem;
  font-weight: bold;
  position: absolute;
}

.dic-card__title {
  flex: 1;
  min-width: 0;
}

.dic-card__name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.35;
}

.dic-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-top: 0.35rem;
}

.dic-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.7rem;
  background: #f1f5f9;
  color: #475569;
  border-radius: 7px;
  font-size: 0.72rem;
  font-weight: 500;
  white-space: nowrap;
}

.dic-chip--info {
  background: #eff6ff;
  color: #1d4ed8;
}

.dic-chip--warning {
  background: #fef3c7;
  color: #92400e;
}

.dic-chip--media {
  background: #f3e8ff;
  color: #7e22ce;
}

.dic-chip--media-video {
  background: #fce7f3;
  color: #be123c;
}

.dic-chip__sub {
  font-weight: 400;
  opacity: 0.8;
}

.dic-card__toggle {
  flex: 0 0 auto;
  width: 2.1rem;
  height: 2.1rem;
  padding: 0;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
}

.dic-card__toggle:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.dic-card__body {
  padding: 0.85rem 1rem 1rem;
  border-top: 1px dashed #e5e7eb;
  background: #fbfdff;
}

.dic-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.65rem;
}

.dic-section-title {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  color: #475569;
  text-transform: uppercase;
}

.dic-camera-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 0.5rem;
}

.dic-camera {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  color: #475569;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  user-select: none;
  margin: 0;
}

.dic-camera input {
  display: none;
}
.dic-camera i {
  color: #94a3b8;
  font-size: 0.85rem;
}
.dic-camera:hover {
  border-color: #93c5fd;
  color: #1d4ed8;
}

.dic-camera.is-active {
  background: #eff6ff;
  border-color: #0d6efd;
  color: #1d4ed8;
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.08);
}

.dic-camera.is-active:has(input:disabled),
.dic-camera input:disabled {
  cursor: not-allowed;
}

.dic-no-cam {
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  background: #fff7ed;
  color: #b45309;
  border: 1px solid #fed7aa;
  font-size: 0.85rem;
}

.dic-inline-error {
  margin-top: 0.5rem;
  color: #b91c1c;
  font-size: 0.8rem;
}

/* MEDIA */
.dic-media {
  margin-bottom: 0.9rem;
  padding: 0.75rem 0.85rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
}

.dic-media--profile {
  background: #fef2f2;
  border-color: #fecaca;
}

.dic-media-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.dic-media-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #f8fafc;
  color: #475569;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
}

.dic-media-btn i {
  font-size: 0.9rem;
  color: #94a3b8;
}

.dic-media-btn.is-active {
  background: linear-gradient(135deg, #0d6efd 0%, #0a5fd7 100%);
  border-color: #0a5fd7;
  color: #fff;
}

.dic-media-btn.is-active i {
  color: #fff;
}

.dic-media-info {
  margin-left: auto;
  font-size: 0.78rem;
  color: #475569;
}

.dic-media-info strong {
  color: #0d6efd;
}

.dic-media-notice {
  margin-left: auto;
  padding: 0.5rem 0.75rem;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 6px;
  font-size: 0.8rem;
  color: #92400e;
}

/* FOOTER */
.dic-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 1rem;
  flex-wrap: wrap;
}

.dic-footer__status {
  font-size: 0.85rem;
}
.dic-footer__actions {
  display: flex;
  gap: 0.5rem;
}

.pulse-dot {
  font-size: 0.55rem;
  animation: pulse 1.4s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}

/* RESPONSIVE */
@media (max-width: 576px) {
  .dic-toolbar__row--between {
    flex-direction: column;
    align-items: flex-start;
  }
  .dic-toolbar__actions {
    width: 100%;
  }
  .dic-camera-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .dic-footer {
    flex-direction: column;
    align-items: stretch;
  }
  .dic-footer__actions {
    justify-content: flex-end;
  }
}
</style>
